import Cookies from 'js-cookie';
import type React from 'react';
import { useEffect, useMemo } from 'react';

import { CookieKey } from '@/constants';
import { useGlobalState } from '@/hooks';
import { LoginMethod } from '@/interfaces';
import {
  CarriersProvider,
  MyParcelsProvider,
  PopUpsProvider,
  RecaptchaProvider,
  ShipmentDetailsProvider,
  ShipmentFiltersProvider,
  ShipmentsOverviewProvider,
  useEmailSyncContext,
} from '@/modules/pmt/context';
import {
  type IMainTranslation,
  type INotificationTranslation,
  type IPmtOnboardingPopup,
  type IPmtPage,
  type ITrackingTranslation,
  PmtSignUpStep,
} from '@/modules/pmt/interfaces';
import { MyParcelsLayout } from '../MyParcelsLayout';

interface IMyParcelsProps {
  mainTranslation: IMainTranslation;
  trackingTranslation: ITrackingTranslation;
  pmtOnboardingPopUp: IPmtOnboardingPopup;
  pmtEmailSyncOnboardingPopUp: IPmtOnboardingPopup;
  notificationTranslation: INotificationTranslation;
  pmtPageData: IPmtPage | null;
}

export const MyParcels: React.FC<IMyParcelsProps> = ({
  pmtPageData,
  mainTranslation,
  trackingTranslation,
  notificationTranslation,
  pmtOnboardingPopUp,
  pmtEmailSyncOnboardingPopUp,
}) => {
  const { userInfo, showPMTPopUp, setShowPMTPopUp, setCurrentPMTSignUpStep, pmtSignUpStep } = useGlobalState();
  const { fetchEmailSyncAccounts } = useEmailSyncContext();

  const pmtProps = useMemo(
    () => ({
      pmtPageData,
      mainTranslation,
      trackingTranslation,
      notificationTranslation,
      pmtOnboardingPopUp,
      pmtEmailSyncOnboardingPopUp,
    }),
    [
      pmtPageData,
      mainTranslation,
      notificationTranslation,
      pmtEmailSyncOnboardingPopUp,
      pmtOnboardingPopUp,
      trackingTranslation,
    ],
  );

  useEffect(() => {
    const pmtVerificationStatus = Cookies.get(CookieKey.PmtVerificationStatus);
    if (pmtVerificationStatus && JSON.parse(pmtVerificationStatus) && !showPMTPopUp) {
      setShowPMTPopUp(true);
    }
  }, [showPMTPopUp, setShowPMTPopUp]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on userInfo; pmtSignUpStep is read inside but not a trigger
  useEffect(() => {
    // *? Should we add pmtSignUpStep to dependencies array
    switch (true) {
      case pmtSignUpStep !== PmtSignUpStep.SignUp && !userInfo:
        setCurrentPMTSignUpStep(PmtSignUpStep.SignUp);
        break;

      case pmtSignUpStep !== PmtSignUpStep.VerifyEmail &&
        userInfo &&
        userInfo.authenticationMethod === LoginMethod.Email &&
        !userInfo.verificationStatus:
        setCurrentPMTSignUpStep(PmtSignUpStep.VerifyEmail);
        break;

      case pmtSignUpStep !== PmtSignUpStep.Success && !!userInfo:
        setCurrentPMTSignUpStep(PmtSignUpStep.Success);
        break;

      default:
        break;
    }
  }, [userInfo]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs once on mount; fetchEmailSyncAccounts is stable
  useEffect(() => {
    (async () => {
      // Fetch email sync data to checking isEmailSyncAllowed
      await fetchEmailSyncAccounts();
    })();
  }, []);

  return (
    <MyParcelsProvider pmtProps={pmtProps}>
      <RecaptchaProvider>
        <CarriersProvider>
          <ShipmentFiltersProvider>
            <ShipmentsOverviewProvider>
              <ShipmentDetailsProvider>
                <PopUpsProvider>
                  <MyParcelsLayout />
                </PopUpsProvider>
              </ShipmentDetailsProvider>
            </ShipmentsOverviewProvider>
          </ShipmentFiltersProvider>
        </CarriersProvider>
      </RecaptchaProvider>
    </MyParcelsProvider>
  );
};
